import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { REGEX } from "@constants/regex";
import { useAppConfig } from "@context/AppConfigContext";
import {
  loginWithCredentialsAPI,
  verifyOTPAPI,
  resendOTPAPI,
} from "@actions/loginAction";

const useLoginPageData = () => {
  const navigate = useNavigate();
  const { loginUser } = useAppConfig();
  const [step, setStep] = useState("credentials");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Password visibility toggle
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Password visibility data
  const passwordVisibilityData = {
    showPassword,
    togglePasswordVisibility,
  };

  // Validation functions
  const validateEmail = (email) => {
    return REGEX.EMAIL.test(email);
  };

  const validateCredentials = () => {
    const newErrors = {};
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    return newErrors;
  };

  // Updated OTP validation for 6 digits
  const validateOTP = () => {
    const newErrors = {};
    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (formData.otp.length !== 6) {
      newErrors.otp = "OTP must be 6 digits";
    }
    return newErrors;
  };

  // Event handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateCredentials();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await loginWithCredentialsAPI(
        formData.email,
        formData.password,
      );

      if (response.success) {
        setStep("otp");
        // Store user data in context and localStorage
        loginUser(response.user);
      } else {
        setErrorMessage(
          response.message || "Invalid email or password. Please try again.",
        );
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateOTP();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await verifyOTPAPI(formData.email, formData.otp);

      if (response.success) {
        navigate("/dashboard");
      } else {
        setErrorMessage(response.message || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await resendOTPAPI(formData.email);
      if (response.success) {
        toast.success(response.message || "OTP Resent Successfully");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong, Please try again!");
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setStep("credentials");
    setFormData((prev) => ({ ...prev, otp: "" }));
    setErrors({});
    setErrorMessage("");
  };

  return {
    step,
    formData,
    errors,
    loading,
    errorMessage,
    handleChange,
    handleCredentialsSubmit,
    handleOTPSubmit,
    handleResendOTP,
    handleBackToLogin,
    passwordVisibilityData,
  };
};

export default useLoginPageData;
